import React, { useState } from "react";

import { Field, Label, Description, ErrorMessage } from "V2.0/common/fieldset";
import { Input } from "V2.0/common/input";
import { Select } from "V2.0/common/select";
import { Button } from "V2.0/common/button";
import { Dialog, DialogTitle, DialogDescription, DialogBody, DialogActions } from "V2.0/common/dialog";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "V2.0/common/table";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/16/solid";

import {
  RequestField,
  SEND_AS_OPTIONS,
  VALUE_SOURCE_OPTIONS,
  ValueSource,
  newRequestField,
  valueSourcesFor,
  describeRequestValue,
} from "../models/requestField";
import { HttpMethod } from "../models/editorState";

type Props = {
  triggerEvent: string;
  apiMethod: HttpMethod;
  apiURL: string;
  requestFields: RequestField[];
  conversationLabel: string;
  variableSuggestions: string[];
  errors: Record<string, string>;
  onChange: (patch: { apiMethod?: HttpMethod; apiURL?: string }) => void;
  onFieldsChange: (fields: RequestField[]) => void;
};

const METHODS: HttpMethod[] = ["GET", "POST", "PUT"];

export function TheRequest({
  triggerEvent,
  apiMethod,
  apiURL,
  requestFields,
  conversationLabel,
  variableSuggestions,
  errors,
  onChange,
  onFieldsChange,
}: Props) {
  const [draft, setDraft] = useState<RequestField | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const sendAsLabel = (value: string) => SEND_AS_OPTIONS.find((option) => option.value === value)?.label ?? value;
  const sourceLabel = (value: string) => VALUE_SOURCE_OPTIONS.find((option) => option.value === value)?.label ?? value;

  const startAdd = () => {
    setDraft(newRequestField());
    setEditingIndex(null);
  };
  const startEdit = (index: number) => {
    setDraft({ ...requestFields[index] });
    setEditingIndex(index);
  };
  const cancel = () => {
    setDraft(null);
    setEditingIndex(null);
  };
  const commit = (field: RequestField) => {
    const next = [...requestFields];
    if (editingIndex === null) next.push(field);
    else next[editingIndex] = field;
    onFieldsChange(next);
    cancel();
  };
  const remove = (index: number) => {
    onFieldsChange(requestFields.filter((_, i) => i !== index));
    if (editingIndex === index) cancel();
  };

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,3fr)]">
        <Field>
          <Label>Method</Label>
          <Select name="apiMethod" value={apiMethod} onChange={(event) => onChange({ apiMethod: event.target.value as HttpMethod })}>
            {METHODS.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <Label>URL</Label>
          <Input
            name="apiURL"
            type="url"
            value={apiURL}
            onChange={(event) => onChange({ apiURL: event.target.value })}
            placeholder="https://"
            invalid={!!errors.apiURL}
          />
          {errors.apiURL && <ErrorMessage>{errors.apiURL}</ErrorMessage>}
        </Field>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-base/6 font-medium text-zinc-950 sm:text-sm/6">Request fields</p>
            <p className="text-base/6 text-zinc-500 sm:text-sm/6">Each field is sent with the request. Choose where it goes and where its value comes from.</p>
          </div>
          <Button outline className="shrink-0" onClick={startAdd}>
            <PlusIcon />
            Add a request field
          </Button>
        </div>

        {requestFields.length > 0 ? (
          <div className="rounded-lg border border-zinc-200 px-4">
            <Table dense>
              <TableHead>
                <TableRow>
                  <TableHeader>Field</TableHeader>
                  <TableHeader>Send as</TableHeader>
                  <TableHeader>Value comes from</TableHeader>
                  <TableHeader>Value</TableHeader>
                  <TableHeader className="w-px" />
                </TableRow>
              </TableHead>
              <TableBody>
                {requestFields.map((field, index) => (
                  <TableRow key={`${field.name}-${index}`}>
                    <TableCell className="font-medium">{field.name}</TableCell>
                    <TableCell>{sendAsLabel(field.sendAs)}</TableCell>
                    <TableCell>{sourceLabel(field.valueSource)}</TableCell>
                    <TableCell className="text-zinc-500">{describeRequestValue(field, { conversationLabel })}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button plain aria-label={`Edit ${field.name}`} onClick={() => startEdit(index)}>
                          <PencilIcon />
                        </Button>
                        <Button plain aria-label={`Remove ${field.name}`} onClick={() => remove(index)}>
                          <TrashIcon />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="rounded-lg border border-dashed border-zinc-200 px-4 py-6 text-center text-sm text-zinc-500">No fields yet. The request is sent as it is.</p>
        )}
      </div>

      <RequestFieldDialog
        draft={draft}
        isNew={editingIndex === null}
        allowedSources={valueSourcesFor(triggerEvent)}
        conversationLabel={conversationLabel}
        variableSuggestions={variableSuggestions}
        onChange={setDraft}
        onCancel={cancel}
        onCommit={commit}
      />
    </>
  );
}

type DialogProps = {
  draft: RequestField | null;
  isNew: boolean;
  allowedSources: ValueSource[];
  conversationLabel: string;
  variableSuggestions: string[];
  onChange: (draft: RequestField) => void;
  onCancel: () => void;
  onCommit: (field: RequestField) => void;
};

function RequestFieldDialog({ draft, isNew, allowedSources, conversationLabel, variableSuggestions, onChange, onCancel, onCommit }: DialogProps) {
  const [error, setError] = useState<string | null>(null);
  const name = (draft?.name ?? "").trim();

  const close = () => {
    setError(null);
    onCancel();
  };

  const submit = () => {
    if (!draft) return;
    if (!name) {
      setError("Give the field a name.");
      return;
    }
    setError(null);
    onCommit({ ...draft, name });
  };

  const sourceDescription = (source: ValueSource) =>
    source === "conversation" ? conversationLabel : VALUE_SOURCE_OPTIONS.find((option) => option.value === source)?.description ?? "";

  return (
    <Dialog open={draft !== null} onClose={close} size="lg">
      {draft && (
        <>
          <DialogTitle>{isNew ? "Add a request field" : "Edit request field"}</DialogTitle>
          <DialogDescription>Sent with every request this API makes.</DialogDescription>
          <DialogBody className="flex flex-col gap-6">
            <Field>
              <Label>Field name</Label>
              <Input
                name="requestFieldName"
                value={draft.name}
                onChange={(event) => {
                  setError(null);
                  onChange({ ...draft, name: event.target.value });
                }}
                invalid={!!error}
              />
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </Field>

            <Field>
              <Label>Send as</Label>
              <Select name="requestFieldSendAs" value={draft.sendAs} onChange={(event) => onChange({ ...draft, sendAs: event.target.value as RequestField["sendAs"] })}>
                {SEND_AS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field>
              <Label>Value comes from</Label>
              <Select
                name="requestFieldValueSource"
                value={draft.valueSource}
                onChange={(event) => onChange({ ...draft, valueSource: event.target.value as ValueSource })}
              >
                {VALUE_SOURCE_OPTIONS.filter((option) => allowedSources.includes(option.value)).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Description>{sourceDescription(draft.valueSource)}</Description>
            </Field>

            {draft.valueSource === "fixed" && (
              <Field>
                <Label>Fixed value</Label>
                <Input
                  name="requestFieldFixedValue"
                  type={draft.sendAs === "header" ? "password" : "text"}
                  value={draft.fixedValue}
                  onChange={(event) => onChange({ ...draft, fixedValue: event.target.value })}
                />
              </Field>
            )}

            {draft.valueSource === "callVariable" && (
              <Field>
                <Label>Which call variable</Label>
                <Input
                  name="requestFieldVariable"
                  list="request-field-variable-suggestions"
                  value={draft.variableName}
                  placeholder={name || "Same as the field name"}
                  onChange={(event) => onChange({ ...draft, variableName: event.target.value })}
                />
                <Description>
                  Details known when the call starts, such as the caller's phone number and the call ID, and anything an earlier API in this
                  call saved for later requests. Leave empty to use the field name.
                </Description>
                {variableSuggestions.length > 0 && (
                  <datalist id="request-field-variable-suggestions">
                    {variableSuggestions.map((suggestion) => (
                      <option key={suggestion} value={suggestion} />
                    ))}
                  </datalist>
                )}
              </Field>
            )}
          </DialogBody>
          <DialogActions>
            <Button plain onClick={close}>
              Cancel
            </Button>
            <Button onClick={submit}>{isNew ? "Add field" : "Save field"}</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
